import React from 'react'
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Alert,
  Tooltip
} from '@mui/material'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'
import ButtonInc from '../../../../../shared-ui/ButtonInc'
import {
  CardHeader,
  detailCard,
  labelStyle,
  linkStyle,
  rowStyle,
  safeText,
  titleCase,
  valueStyle
} from './ui'

// The group service's GroupDocumentType enum.
export const DOCUMENT_TYPES = [
  {
    value: 'certificate_of_registration',
    label: 'Certificate of Registration'
  },
  {
    value: 'directors_shareholders_or_officials_list',
    label: 'Directors / Shareholders / Officials List'
  },
  { value: 'directors_id_copies', label: 'Directors / Officials ID Copies' },
  { value: 'group_tax_pin', label: 'Group KRA PIN Certificate' },
  { value: 'other', label: 'Other Document' }
]

// The service caps uploads at 10 MB; reject oversized files here so the user
// is not left waiting on an S3 rejection.
const MAX_UPLOAD_SIZE_BYTES = 10 * 1024 * 1024

export function documentTypeLabel(name) {
  const known = DOCUMENT_TYPES.find(function(type) {
    return type.value === name
  })
  return known ? known.label : titleCase(name)
}

// Documents come back with a presigned URL whose path still ends in the key we
// uploaded: `groups/<partner>/<group>/<timestamp>_<filename>`.
export function documentFileName(doc) {
  if (!doc || !doc.url) return null
  let path = doc.url
  try {
    path = new URL(doc.url).pathname
  } catch (e) {
    // Not an absolute URL — treat the value as a bare bucket key.
  }
  let last = path.split('/').pop() || ''
  try {
    last = decodeURIComponent(last)
  } catch (e) {
    // Leave the raw segment if it is not valid percent-encoding.
  }
  return last.replace(/^\d+_/, '') || null
}

function formatDate(value) {
  return value ? new Date(value).toLocaleDateString() : null
}

// One uploaded file: what it is called, when it landed, and its actions.
function DocumentRow(props) {
  const doc = props.doc
  const uploadedOn = formatDate(doc.created_at)
  return (
    <Box
      mb={1}
      p={1.5}
      style={rowStyle}
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      gap={2}
    >
      <Box display="flex" alignItems="center" gap={1} minWidth={0}>
        <CheckCircleOutlineIcon
          style={{ color: '#0A7D3A', fontSize: '18px', flexShrink: 0 }}
        />
        <div style={{ minWidth: 0 }}>
          <h6
            style={Object.assign({}, valueStyle, {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            })}
          >
            {safeText(documentFileName(doc) || documentTypeLabel(doc.name))}
          </h6>
          {uploadedOn && <p style={labelStyle}>Uploaded {uploadedOn}</p>}
        </div>
      </Box>
      <Box display="flex" alignItems="center" gap={1} flexShrink={0}>
        {doc.url && (
          <a
            href={doc.url}
            target="_blank"
            rel="noopener noreferrer"
            style={linkStyle}
          >
            View <OpenInNewIcon style={{ fontSize: '14px' }} />
          </a>
        )}
        <Tooltip title="Delete document">
          <IconButton
            size="small"
            aria-label={`Delete ${documentTypeLabel(doc.name)}`}
            onClick={function() {
              props.onDelete(doc)
            }}
          >
            <DeleteOutlineIcon style={{ color: '#8F191C', fontSize: '18px' }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  )
}

// A document type and every file uploaded against it — required types show up
// even with nothing uploaded, so the reviewer can see what is still missing.
function DocumentGroup(props) {
  const docs = props.docs
  return (
    <Box mb={2}>
      <Box
        mb={1}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap={2}
      >
        <Box display="flex" alignItems="center" gap={1}>
          {docs.length === 0 && (
            <CancelOutlinedIcon
              style={{ color: '#8D8D8C', fontSize: '18px' }}
            />
          )}
          <h6 style={valueStyle}>{documentTypeLabel(props.name)}</h6>
          {docs.length > 1 && (
            <span style={labelStyle}>{docs.length} files</span>
          )}
        </Box>
        <ButtonInc
          btnType="secondary"
          startIcon={<UploadFileIcon style={{ fontSize: '16px' }} />}
          onClick={function() {
            props.onUpload(props.name)
          }}
        >
          Upload
        </ButtonInc>
      </Box>
      {docs.length === 0 ? (
        <Box p={1.5} style={rowStyle}>
          <span style={labelStyle}>Not uploaded</span>
        </Box>
      ) : (
        docs.map(function(doc, i) {
          return (
            <DocumentRow
              key={doc.guid || i}
              doc={doc}
              onDelete={props.onDelete}
            />
          )
        })
      )}
    </Box>
  )
}

class GroupDocuments extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      uploadDialogOpen: false,
      uploadType: DOCUMENT_TYPES[0].value,
      uploadFile: null,
      uploadValidationError: null,
      deleteDialogDoc: null
    }
    this.openUploadDialog = this.openUploadDialog.bind(this)
    this.closeUploadDialog = this.closeUploadDialog.bind(this)
    this.handleFileChange = this.handleFileChange.bind(this)
    this.handleUpload = this.handleUpload.bind(this)
    this.openDeleteDialog = this.openDeleteDialog.bind(this)
    this.closeDeleteDialog = this.closeDeleteDialog.bind(this)
    this.handleDelete = this.handleDelete.bind(this)
  }

  componentWillReceiveProps(nextProps) {
    // Both mutations answer with the refreshed group, so the panel just closes
    // its dialog and clears the one-shot flag.
    if (
      !this.props.upload_document_success &&
      nextProps.upload_document_success
    ) {
      this.setState({
        uploadDialogOpen: false,
        uploadFile: null,
        uploadValidationError: null
      })
      this.props.resetUploadDocument()
    }
    if (
      !this.props.delete_document_success &&
      nextProps.delete_document_success
    ) {
      this.setState({ deleteDialogDoc: null })
      this.props.resetDeleteDocument()
    }
  }

  openUploadDialog(documentType) {
    this.props.resetUploadDocument()
    this.setState({
      uploadDialogOpen: true,
      uploadType: documentType || DOCUMENT_TYPES[0].value,
      uploadFile: null,
      uploadValidationError: null
    })
  }

  closeUploadDialog() {
    this.setState({ uploadDialogOpen: false, uploadValidationError: null })
  }

  handleFileChange(event) {
    const file = event.target.files && event.target.files[0]
    if (!file) {
      this.setState({ uploadFile: null, uploadValidationError: null })
      return
    }
    if (file.size > MAX_UPLOAD_SIZE_BYTES) {
      this.setState({
        uploadFile: null,
        uploadValidationError: 'That file is larger than the 10 MB limit.'
      })
      return
    }
    this.setState({ uploadFile: file, uploadValidationError: null })
  }

  handleUpload() {
    if (!this.state.uploadFile) return
    this.props.uploadDocument({
      guid: this.props.group.guid,
      name: this.state.uploadType,
      file: this.state.uploadFile
    })
  }

  openDeleteDialog(doc) {
    this.props.resetDeleteDocument()
    this.setState({ deleteDialogDoc: doc })
  }

  closeDeleteDialog() {
    this.setState({ deleteDialogDoc: null })
  }

  handleDelete() {
    const doc = this.state.deleteDialogDoc
    if (!doc) return
    this.props.deleteDocument({
      guid: this.props.group.guid,
      docGuid: doc.guid
    })
  }

  // Required types first (in the order the product asks for them), then any
  // type that was uploaded without being required.
  documentGroups() {
    const group = this.props.group
    const required = group.required_docs_array || []
    const uploaded = group.registration_documents || []
    const extras = uploaded
      .map(function(doc) {
        return doc.name
      })
      .filter(function(name, i, names) {
        return required.indexOf(name) === -1 && names.indexOf(name) === i
      })
    return required.concat(extras).map(function(name) {
      return {
        name,
        required: required.indexOf(name) !== -1,
        docs: uploaded.filter(function(doc) {
          return doc.name === name
        })
      }
    })
  }

  renderUploadDialog() {
    const file = this.state.uploadFile
    const error =
      this.state.uploadValidationError || this.props.upload_document_error
    return (
      <Dialog
        open={this.state.uploadDialogOpen}
        onClose={this.closeUploadDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{ style: { borderRadius: '8px', padding: '8px' } }}
      >
        <DialogTitle style={{ fontFamily: 'Lato', fontWeight: 600 }}>
          Upload Document
        </DialogTitle>
        <DialogContent>
          <Box mt={1} display="flex" flexDirection="column" gap={2}>
            {error && (
              <Alert severity="error" style={{ borderRadius: '8px' }}>
                {safeText(error)}
              </Alert>
            )}
            <FormControl fullWidth size="small">
              <InputLabel>Document Type</InputLabel>
              <Select
                value={this.state.uploadType}
                label="Document Type"
                onChange={function(e) {
                  this.setState({ uploadType: e.target.value })
                }.bind(this)}
              >
                {DOCUMENT_TYPES.map(function(type) {
                  return (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  )
                })}
              </Select>
            </FormControl>
            <Box display="flex" alignItems="center" gap={2}>
              <ButtonInc
                btnType="secondary"
                component="label"
                startIcon={<UploadFileIcon style={{ fontSize: '16px' }} />}
              >
                Choose File
                <input
                  type="file"
                  hidden
                  accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                  onChange={this.handleFileChange}
                />
              </ButtonInc>
              <span style={file ? valueStyle : labelStyle}>
                {file ? file.name : 'No file selected'}
              </span>
            </Box>
            <span style={labelStyle}>
              PDF, image or Word document, up to 10 MB.
            </span>
          </Box>
        </DialogContent>
        <DialogActions style={{ padding: '16px' }}>
          <ButtonInc
            btnType="secondary"
            onClick={this.closeUploadDialog}
            disabled={this.props.upload_document_loader}
          >
            Cancel
          </ButtonInc>
          <ButtonInc
            variant="contained"
            onClick={this.handleUpload}
            loading={this.props.upload_document_loader}
            disabled={this.props.upload_document_loader || !file}
          >
            Upload
          </ButtonInc>
        </DialogActions>
      </Dialog>
    )
  }

  renderDeleteDialog() {
    const doc = this.state.deleteDialogDoc
    return (
      <Dialog
        open={Boolean(doc)}
        onClose={this.closeDeleteDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{ style: { borderRadius: '8px', padding: '8px' } }}
      >
        <DialogTitle style={{ fontFamily: 'Lato', fontWeight: 600 }}>
          Delete Document
        </DialogTitle>
        <DialogContent>
          <Box mt={1} display="flex" flexDirection="column" gap={2}>
            {this.props.delete_document_error && (
              <Alert severity="error" style={{ borderRadius: '8px' }}>
                {safeText(this.props.delete_document_error)}
              </Alert>
            )}
            <span style={{ fontFamily: 'Open Sans', fontSize: '13px' }}>
              Are you sure you want to delete{' '}
              <strong>
                {doc
                  ? documentFileName(doc) || documentTypeLabel(doc.name)
                  : ''}
              </strong>
              ? This cannot be undone.
            </span>
          </Box>
        </DialogContent>
        <DialogActions style={{ padding: '16px' }}>
          <ButtonInc
            btnType="secondary"
            onClick={this.closeDeleteDialog}
            disabled={this.props.delete_document_loader}
          >
            Cancel
          </ButtonInc>
          <ButtonInc
            variant="contained"
            onClick={this.handleDelete}
            loading={this.props.delete_document_loader}
            disabled={this.props.delete_document_loader}
          >
            Confirm Delete
          </ButtonInc>
        </DialogActions>
      </Dialog>
    )
  }

  render() {
    const group = this.props.group
    const groups = this.documentGroups()
    const requiredGroups = groups.filter(function(entry) {
      return entry.required
    })
    const extraGroups = groups.filter(function(entry) {
      return !entry.required
    })
    return (
      <div style={detailCard}>
        <CardHeader
          title="Documents"
          status={group.docs_complete ? 'Complete' : 'Pending'}
          action={
            <ButtonInc
              variant="contained"
              startIcon={<UploadFileIcon style={{ fontSize: '16px' }} />}
              onClick={function() {
                this.openUploadDialog()
              }.bind(this)}
            >
              Upload Document
            </ButtonInc>
          }
        />

        {/* An upload or delete launched from a row surfaces its error here so
            it is visible even after the dialog closes. */}
        {this.props.delete_document_error && !this.state.deleteDialogDoc && (
          <Alert
            severity="error"
            style={{ marginBottom: '16px', borderRadius: '8px' }}
          >
            {safeText(this.props.delete_document_error)}
          </Alert>
        )}

        <p style={Object.assign({}, labelStyle, { marginBottom: 12 })}>
          Required Documents
        </p>
        {requiredGroups.length === 0 ? (
          <p style={labelStyle}>No documents required.</p>
        ) : (
          requiredGroups.map(
            function(entry) {
              return (
                <DocumentGroup
                  key={entry.name}
                  name={entry.name}
                  docs={entry.docs}
                  onUpload={this.openUploadDialog}
                  onDelete={this.openDeleteDialog}
                />
              )
            }.bind(this)
          )
        )}

        {extraGroups.length > 0 && (
          <div>
            <p style={Object.assign({}, labelStyle, { margin: '18px 0 12px' })}>
              Additional Documents
            </p>
            {extraGroups.map(
              function(entry) {
                return (
                  <DocumentGroup
                    key={entry.name}
                    name={entry.name}
                    docs={entry.docs}
                    onUpload={this.openUploadDialog}
                    onDelete={this.openDeleteDialog}
                  />
                )
              }.bind(this)
            )}
          </div>
        )}

        {this.renderUploadDialog()}
        {this.renderDeleteDialog()}
      </div>
    )
  }
}

export default GroupDocuments
